import React, { useCallback, useEffect, useRef, useState } from 'react';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';
import classes from 'components/common/TitleEditName/TitleEditName.module.scss';

interface Props {
    value: string;
    onChange: (value: string) => void;
    isLoading?: boolean;
    maxLength?: number;
}

export const TitleEditName = ({ value, onChange, isLoading, maxLength }: Props) => {
    const [editValue, setEditValue] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const ref = useRef<HTMLInputElement | null>(null);

    // Memoized function to handle value change
    const onChangeValue = useCallback(
        (newValue: string) => {
            if (value !== newValue) {
                onChange(newValue);
            }
        },
        [value, onChange],
    );

    // Handle key down events for Enter and Escape keys
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onChangeValue(editValue);
            setIsEditing(false);
        } else if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    // Handle click on the text to enable editing
    const onClick = () => {
        setIsEditing(true);
    };

    // Handle click outside to save changes
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onChangeValue(editValue);
                setIsEditing(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editValue, onChangeValue]);

    // Sync editValue with value prop changes
    useEffect(() => {
        setEditValue(value);
    }, [value]);

    return (
        <div className={classes.container}>
            {isEditing ? (
                <input
                    type={'text'}
                    value={editValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    className={classes.reportNameInput}
                    autoFocus
                    ref={ref}
                    {...(maxLength !== undefined ? { maxLength } : {})}
                />
            ) : (
                <div className={classes.reportName} onClick={onClick} title={'Click to edit'}>
                    <span className={classes.value}>{value}</span>
                    {isLoading ? (
                        <div className={classes.spinnerWrapper}>
                            <LoaderSpinner />
                        </div>
                    ) : (
                        <MuiIcon icon={'edit'} fontSize={'20px'} className={classes.editIcon} type={'outlined'} />
                    )}
                </div>
            )}
        </div>
    );
};
